// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { Link } from 'react-router-dom';
import { PrimaryButton } from 'office-ui-fabric-react';
import { useTranslation } from 'react-i18next';
import React from 'react';
import {
    useSelector
} from 'react-redux';
import { userInfoSelector } from './../../store/user/selector';

import dateFormat from 'dateformat';
import Styles from './warranties.module.scss';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { featureFlagsSelector } from 'src/store/config/selector';

const ACCIDENTAL_DAMAGE_PROTECTION = 'ADP';
const ADVANCED_EXCHANGE_SERVICE = 'AES';
const EXTENDED_PLAN = 'EEC';
const EXTENDED_SERVICE_PLAN = 'ESP';
const KEEPYOUR_OWNDEVICE_PLAN = 'KD1';
const REPAIR_WARRANTY_PLAN = 'RPS';
const STANDARD_WARRANTY = 'STD';
const TECHNICAL_SUPPORT_PLAN = 'TSP';
const VALUE_ADDED_SERVICE = 'VAS';

const Warranties = (props: any) => {
    const featureFlags = useSelector(state => featureFlagsSelector(state));
    const { t } = useTranslation();
    const { disabled, checked, warranties, status, claimDetails } = props;
    const renderPlanName = (plan: string, planType: string): any => {
        switch (plan.toUpperCase()) {
            case ACCIDENTAL_DAMAGE_PROTECTION:
                return t('accidental_damage_protection');
            case ADVANCED_EXCHANGE_SERVICE:
                return t('advanced_exchange_service');
            case EXTENDED_PLAN:
                return t('extended_plan');
            case EXTENDED_SERVICE_PLAN:
                return t('extended_service_plan');
            case KEEPYOUR_OWNDEVICE_PLAN:
                return t('keepyour_owndevice_plan');
            case REPAIR_WARRANTY_PLAN:
                return t('repair_warranty_plan');
            case STANDARD_WARRANTY:
                return t('standard_warranty');
            case TECHNICAL_SUPPORT_PLAN:
                return t('technical_support_plan');
            case VALUE_ADDED_SERVICE:
                return t('value_added_service');
            case '':
            case null:
            case undefined:
                return t('no_warranties_available_message');
            default:
                return planType;
        }
    };

    const findIfMaxClaimLimitHasReached = (scid: string, entitlement: string) => {
        if(featureFlags?.claimOrderV2LabelFeatureFlag){
            if ((entitlement === 'ADP' || entitlement === 'BRS'  || entitlement === 'TSP' || entitlement === 'WE3') && scid && scid !== null && claimDetails && claimDetails !== null) {
                const claimDetail = claimDetails.filter((item: any) => { return item.serviceContractID === scid && item.entitlementCode === entitlement; });
                if (claimDetail !== null && claimDetail.length > 0 && (claimDetail[0].inwarrantyClaimsLeft ?? 'NA') === '0') {
                    return true;
                }
            }
            return false;
        }
        else{
            if ((entitlement === 'ADP' || entitlement === 'ESP'  || entitlement === 'TSP') && scid && scid !== null && claimDetails && claimDetails !== null) {
                const claimDetail = claimDetails.filter((item: any) => { return item.serviceContractID === scid; });
                if (claimDetail !== null && claimDetail.length > 0 && (claimDetail[0].inwarrantyClaimsLeft ?? 'NA') === '0') {
                    return true;
                }
            }
            return false;
        }
    };

    const findDateDifference = (endDate: Date) => {
        const today: any = new Date();
        const end: any = new Date(endDate);
        const diffDays = (end - today) / (1000 * 60 * 60 * 24);
        if (diffDays <= 0) {
            return 0;
        }
        return diffDays.toFixed(0);
    };

    const userInfo = useSelector(state => userInfoSelector(state));
    const renderWarranties = (<div className={Styles.warrantiesSec}>
        <div className={Styles.planDetail}>
            {
                warranties && warranties.map((warranty: any, i: number) => {
                    return (
                        <div className={Styles.plan} key={i}>
                            <div className={`${Styles.fontSize} ${Styles.planWidth}`}>{renderPlanName(warranty.serviceContractTypeCode, warranty.serviceContractType)}</div>
                            <div className={`${Styles.fontSizeSmall} ${Styles.planWidth}`}>
                                <div>
                                    <span>{t('start_date')}: </span>
                                    <span className={Styles.fontBold}>{dateFormat(warranty.startDate, 'mediumDate')}</span>
                                </div>
                                <div>
                                    <span>{t('duration')}: </span>
                                    <span className={Styles.fontBold}>{t('duration_days_left', { Number: findDateDifference(warranty.endDate) })}</span>
                                </div>
                            </div>
                            <div className={`${Styles.fontSizeSmall} ${Styles.planWidth}`}>
                                <div>
                                    <span>{t('end_date')}: </span>
                                    <span className={Styles.fontBold}>{dateFormat(warranty.endDate, 'mediumDate')}</span>
                                </div>
                            </div>
                            <div className={`${Styles.fontSizeSmall} ${Styles.statusContainer} `}>
                                <span>{t('status')}</span>
                                {
                                    findIfMaxClaimLimitHasReached(warranty.id, warranty.serviceContractTypeCode) === true || findDateDifference(warranty.endDate) === 0 ?
                                        <span className={Styles.expiredMark}>&times;</span> :
                                        <span className={Styles.checkMark}>
                                            <span className={Styles.checkMark_circle} />
                                            <span className={Styles.checkMark_stem} />
                                            <span className={Styles.checkMark_kick} />
                                        </span>
                                }
                                <span>
                                    {findIfMaxClaimLimitHasReached(warranty.id, warranty.serviceContractTypeCode) === true
                                        || findDateDifference(warranty.endDate) === 0
                                        ? t('expired')
                                        : featureFlags?.installBaseWarrantyUpdateFeatureFlag ? t('active') : status}
                                </span>
                                {
                                    (findIfMaxClaimLimitHasReached(warranty.id, warranty.serviceContractTypeCode) === true) &&
                                    <span title={`${t('avail_claim_count')} 0`}>
                                        <Icon className={Styles.infoMark} iconName="info" />
                                    </span>
                                }

                            </div>
                        </div>
                    );
                })
            }

        </div>
        <div className={Styles.extensionButton}>
            {(userInfo.isWarrantyUpdateEnabled) ?
                <Link to={{ pathname: '/device/warranty', state: props }}>
                    <PrimaryButton
                        data-automation-id="test"
                        allowDisabledFocus={true}
                        disabled={disabled}
                        checked={checked}
                        text={t('update_warranty')}
                        className={Styles.extensionWarranty}
                    />
                </Link>
                : null}
        </div>
    </div>);

    const renderMessage = (<div className={Styles.message}>
        {t('no_warranties_available_message')}
    </div>);

    const warrantiesList = () => {
        if (warranties && warranties.length) {
            return renderWarranties;
        }
        return renderMessage;
    };
    return (
        <section data-testid="warranties" className={Styles.warranties}>
            <h2 className={Styles.heading} data-testid="warranty">{t('warranties')}</h2>
            {warrantiesList()}
        </section>
    );
};

export default Warranties;